import React, { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { ResponsiveHeatMapCanvas } from "@nivo/heatmap";
import { throttle } from "lodash";
import Tippy from "@tippyjs/react";
import { followCursor } from "tippy.js";
import { HiExclamationCircle, HiInformationCircle } from "react-icons/hi2";

// -------------------------------------------------------------
// Data & Build Function
// -------------------------------------------------------------
const perkinsStateTargets = {
  "1S1": [0.836, 0.847, 0.857, 0.868],
  "1S2": [0.866, 0.877, 0.887, 0.898],
  "2S1": [0.764, 0.787, 0.809, 0.832],
  "2S2": [0.571, 0.626, 0.681, 0.736],
  "2S3": [0.502, 0.568, 0.635, 0.701],
  "3S1": [0.67, 0.702, 0.734, 0.766],
  "4S1": [0.518, 0.548, 0.578, 0.608],
  "5S1": [0.43, 0.46, 0.49, 0.52],
  "5S2": [0.815, 0.829, 0.843, 0.857],
  "5S3": [0.9, 0.914, 0.928, 0.942],
};

// Indicator descriptions
const indicatorDescriptions = {
  "1S1": "Four-Year Graduation Rate",
  "1S2": "Extended Graduation Rate",
  "2S1": "Academic Proficiency in Reading/Language Arts",
  "2S2": "Academic Proficiency in Mathematics",
  "2S3": "Academic Proficiency in Science",
  "3S1": "Post-Program Placement",
  "4S1": "Non-traditional Program Concentration",
  "5S1": "Program Quality – Attained Recognized Postsecondary Credential",
  "5S2": "Program Quality – Attained Postsecondary Credits",
  "5S3": "Program Quality – Participated in Work-Based Learning",
};

// Special populations as defined in Perkins V
const specialPopulations = [
  "Individuals with disabilities",
  "Economically disadvantaged",
  "Individuals preparing for non-traditional fields",
  "Single parents",
  "Out-of-workforce individuals",
  "English learners",
  "Homeless individuals",
  "Foster care youth",
  "Youth with parents in active military",
];

// Demographic categories
const demographicGroups = [
  "All Students",
  "Female",
  "Male",
  "American Indian/Alaska Native",
  "Asian",
  "Black/African American",
  "Hispanic/Latino",
  "Native Hawaiian/Pacific Islander", 
  "White",
  "Two or More Races",
];

const allIndicators = Object.keys(perkinsStateTargets);

// Function to determine if a group is in a special population
const isSpecialPopulation = (group) => {
  return specialPopulations.some((pop) =>
    group.toLowerCase().includes(pop.toLowerCase())
  );
};

// Function to determine if a group is a demographic group
const isDemographicGroup = (group) => {
  return demographicGroups.some(
    (demo) => group.toLowerCase() === demo.toLowerCase()
  );
};

/**
 * Enhanced version of the original buildSingleYearHeatmapData function.
 * Now categorizes groups into demographics, special populations, and others.
 */
function buildEnhancedHeatmapData(perkinsData, yearIndex) {
  // Create separate row collections for demographics and special populations
  const demographicRows = {};
  const specialPopRows = {};
  const otherRows = {};

  // First, collect all data and categorize
  Object.keys(perkinsData).forEach((districtKey) => {
    const entries = perkinsData[districtKey].data;
    entries.forEach((entry) => {
      const group = entry.studentGroup;
      const indicator = entry.subcategory;

      if (!allIndicators.includes(indicator)) return;

      // Observed array is typically in entry.data[0].pct.
      // Index 0 is 2019, so index (yearIndex + 1) corresponds to 2020..2023.
      const observed = entry.data?.[0]?.pct;
      if (!observed || observed.length < yearIndex + 1) return;

      const obsValue = observed[yearIndex + 1];
      const target = perkinsStateTargets[indicator]?.[yearIndex];

      let diff = null;
      if (typeof obsValue === "number" && typeof target === "number") {
        diff = obsValue - target;
      }

      // Get numerator and denominator if available
      const numerator = entry.data?.[0]?.numerator?.[yearIndex + 1] || null;
      const denominator = entry.data?.[0]?.denominator?.[yearIndex + 1] || null;

      // Add additional data for tooltip and analysis
      const cellData = {
        x: indicator,
        y: diff,
        value: diff,
        actual: obsValue,
        target,
        numerator,
        denominator,
        indicator,
        group,
      };

      // Sort into appropriate category
      let targetCollection;
      if (isDemographicGroup(group)) {
        targetCollection = demographicRows;
      } else if (isSpecialPopulation(group)) {
        targetCollection = specialPopRows;
      } else {
        targetCollection = otherRows;
      }

      if (!targetCollection[group]) {
        targetCollection[group] = { id: group, data: [] };
      }
      targetCollection[group].data.push(cellData);
    });
  });

  // Calculate largest gaps for indicators in each group
  const largestGaps = {};
  allIndicators.forEach((indicator) => {
    largestGaps[indicator] = { value: 0, group: null };

    // Check demographics
    Object.values(demographicRows).forEach((row) => {
      const cell = row.data.find((d) => d.x === indicator);
      if (cell && cell.value !== null && cell.value < largestGaps[indicator].value) {
        largestGaps[indicator] = { value: cell.value, group: row.id };
      }
    });

    // Check special populations
    Object.values(specialPopRows).forEach((row) => {
      const cell = row.data.find((d) => d.x === indicator);
      if (cell && cell.value !== null && cell.value < largestGaps[indicator].value) {
        largestGaps[indicator] = { value: cell.value, group: row.id };
      }
    });
  });

  // Add category flags to each row and mark rows with the largest gaps
  Object.values(demographicRows).forEach((row) => {
    row.category = "demographic";
    row.data.forEach((cell) => {
      cell.hasLargestGap = largestGaps[cell.x]?.group === row.id;
    });
  });
  Object.values(specialPopRows).forEach((row) => {
    row.category = "specialPop";
    row.data.forEach((cell) => {
      cell.hasLargestGap = largestGaps[cell.x]?.group === row.id;
    });
  });
  Object.values(otherRows).forEach((row) => {
    row.category = "other";
    row.data.forEach((cell) => {
      cell.hasLargestGap = largestGaps[cell.x]?.group === row.id;
    });
  });

  // Create spacers between categories
  const demographicSpacer = { id: "spacer-demographics", data: [], category: "spacer" };
  const specialPopSpacer = { id: "spacer-specialpops", data: [], category: "spacer" };

  // Generate data for spacers (one cell per indicator)
  allIndicators.forEach((indicator) => {
    demographicSpacer.data.push({ x: indicator, y: null, value: null });
    specialPopSpacer.data.push({ x: indicator, y: null, value: null });
  });

  // Combine all categories with spacers between them
  return [
    ...Object.values(demographicRows),
    demographicSpacer,
    ...Object.values(specialPopRows),
    specialPopSpacer,
    ...Object.values(otherRows),
  ];
}

// -------------------------------------------------------------
// Enhanced HeatmapTooltip Component
// -------------------------------------------------------------
const EnhancedHeatmapTooltip = ({ cell, averageValue = 0 }) => {
  if (!cell) return null;

  const { serieId: group, data = {}, value, actual, target, numerator, denominator, indicator } = cell;

  // Determine status based on gap value
  let status = "Unknown";
  let statusColor = "text-gray-700";
  let statusBg = "bg-gray-100";

  if (value !== null) {
    if (value < -0.05) {
      status = "Significant Gap";
      statusColor = "text-red-700";
      statusBg = "bg-red-100";
    } else if (value < 0) {
      status = "Below Target";
      statusColor = "text-orange-700";
      statusBg = "bg-orange-100";
    } else if (value <= 0.05) {
      status = "At Target";
      statusColor = "text-green-700";
      statusBg = "bg-green-100";
    } else {
      status = "Above Target";
      statusColor = "text-blue-700";
      statusBg = "bg-blue-100";
    }
  }

  // Format percentages
  const actualPct = actual !== null ? `${(actual * 100).toFixed(1)}%` : "—";
  const targetPct = target !== null ? `${(target * 100).toFixed(1)}%` : "—";
  const diffPct = value !== null ? `${(value * 100).toFixed(1)}%` : "—";

  // Check if this cell represents the largest gap for this indicator
  const isLargestGap = data.hasLargestGap;

  return (
    <div className="p-2 rounded shadow-md border bg-white max-w-xs">
      <div className="flex items-center justify-between mb-1">
        <div className="font-bold text-gray-800">{group}</div>
        {isLargestGap && (
          <div className="text-xs bg-red-100 text-red-700 px-1 py-0.5 rounded font-medium flex items-center">
            <HiExclamationCircle className="mr-1" />
            Largest Gap
          </div>
        )}
      </div>

      <div className="text-sm text-gray-600 mb-2">
        {indicator} - {indicatorDescriptions[indicator] || ""}
      </div>

      <div className={`px-2 py-1 rounded ${statusBg} ${statusColor} text-sm font-medium inline-block mb-2`}>
        {status}
      </div>

      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="flex flex-col">
          <span className="text-gray-500">Actual</span>
          <span className="font-medium">{actualPct}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-gray-500">Target</span>
          <span className="font-medium">{targetPct}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-gray-500">Gap</span>
          <span className={`font-medium ${value < 0 ? "text-red-600" : value > 0 ? "text-blue-600" : "text-gray-600"}`}>
            {diffPct}
          </span>
        </div>
        {numerator !== null && denominator !== null && (
          <div className="flex flex-col">
            <span className="text-gray-500">Count</span>
            <span className="font-medium">
              {numerator} / {denominator}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// -------------------------------------------------------------
// Enhanced Perkins Heatmap Component
// -------------------------------------------------------------
const EnhancedPerkinsHeatmap = ({
  perkinsData,
  selectedYearIndex = 3, // Default to 2023
  leftMargin = 180,
  topMargin = 50,
  containerWidth = 1000,
  containerHeight = 800,
  title = "Perkins Performance Gaps by Group",
  showLegend = true,
  showHighlighting = true,
}) => {
  const [hoveredContent, setHoveredContent] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const containerRef = useRef(null);
  const cellsRef = useRef([]);

  // Year labels for context
  const yearLabels = ["2020", "2021", "2022", "2023"];
  const currentYear = yearLabels[selectedYearIndex];

  // Process data for the heatmap
  const heatmapData = useMemo(() => buildEnhancedHeatmapData(perkinsData, selectedYearIndex), [
    perkinsData,
    selectedYearIndex,
  ]);

  // Calculate summary statistics about gaps
  useEffect(() => {
    if (!heatmapData) return;

    // Find groups with the most indicators below target and count disparities per indicator
    const groupGapCounts = {};
    const indicatorGapCounts = {};
    const largestGapsByIndicator = {};

    heatmapData.forEach((row) => {
      if (row.category === "spacer") return;
      const groupId = row.id;
      groupGapCounts[groupId] = 0;

      row.data.forEach((cell) => {
        const { x: indicator, value } = cell;

        // Count gaps for this group
        if (value !== null && value < 0) {
          groupGapCounts[groupId]++;

          // Count how many groups are below target for this indicator
          indicatorGapCounts[indicator] = (indicatorGapCounts[indicator] || 0) + 1;

          // Track largest gap for each indicator
          if (!largestGapsByIndicator[indicator] || value < largestGapsByIndicator[indicator].value) {
            largestGapsByIndicator[indicator] = { value, group: groupId };
          }
        }
      });
    });

    // Sort groups by gap count (top 5)
    const sortedGroups = Object.entries(groupGapCounts)
      .filter(([_, count]) => count > 0)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    // Sort indicators by number of groups below target (top 5)
    const sortedIndicators = Object.entries(indicatorGapCounts)
      .filter(([_, count]) => count > 0)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    setSummaryData({
      topGroupsWithGaps: sortedGroups,
      topIndicatorsWithGaps: sortedIndicators,
      largestGapsByIndicator,
    });
  }, [heatmapData]);

  // -----------------------------------------------------------
  // Mouse move & hit-testing (for tooltip)
  // -----------------------------------------------------------
  const handleMouseMove = useCallback((e) => {
    const canvas = containerRef.current?.querySelector("canvas");
    if (!canvas) {
      setHoveredContent(null);
      return;
    }
    const rect = canvas.getBoundingClientRect();
    const relX = (e.clientX - rect.left) * (canvas.width / rect.width);
    const relY = (e.clientY - rect.top) * (canvas.height / rect.height);

    let foundCell = null;
    for (const cell of cellsRef.current) {
      const { deviceX, deviceY, deviceW, deviceH } = cell;
      if (
        relX >= deviceX &&
        relX <= deviceX + deviceW &&
        relY >= deviceY &&
        relY <= deviceY + deviceH
      ) {
        foundCell = cell;
        break;
      }
    }
    if (!foundCell || foundCell.serieId?.includes("spacer")) {
      setHoveredContent(null);
      return;
    }
    setHoveredContent(<EnhancedHeatmapTooltip cell={foundCell} />);
  }, []);

  const handleMouseMoveThrottled = useMemo(() => throttle(handleMouseMove, 16), [handleMouseMove]);

  const handleMouseLeave = useCallback(() => {
    setHoveredContent(null);
  }, []);

  // -----------------------------------------------------------
  // Custom Layers for the Heatmap
  // -----------------------------------------------------------

  // 1) Pattern Background Layer: fills the entire canvas with a diagonal pattern
  const PatternBackgroundLayer = (ctx) => {
    const canvasWidth = ctx.canvas.width;
    const canvasHeight = ctx.canvas.height;
    const patternCanvas = document.createElement("canvas");
    patternCanvas.width = 8;
    patternCanvas.height = 8;
    const pctx = patternCanvas.getContext("2d");

    pctx.fillStyle = "#ffffff";
    pctx.fillRect(0, 0, 8, 8);

    pctx.strokeStyle = "#ddd";
    pctx.lineWidth = 0.5;
    pctx.beginPath();
    pctx.moveTo(0, 0);
    pctx.lineTo(8, 8);
    pctx.stroke();

    const diagonalPattern = ctx.createPattern(patternCanvas, "repeat");
    ctx.save();
    ctx.fillStyle = diagonalPattern;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    ctx.restore();
  };

  // 2) Category Headers Layer: adds visual labels for demographic/special population sections
  const CategoryHeadersLayer = (ctx, { cells, margin }) => {
    if (!cells || cells.length === 0) return;
    ctx.save();
    if (margin) {
      ctx.translate(margin.left, margin.top);
    }

    // Determine Y-positions for the first cell of each category (with an offset)
    let demographicY = null;
    let specialPopY = null;
    let otherY = null;
    for (const cell of cells) {
      const row = heatmapData.find((r) => r.id === cell.serieId);
      if (!row) continue;
      if (row.category === "demographic" && demographicY === null) {
        demographicY = cell.y - cell.height / 2 - 10;
      } else if (row.category === "specialPop" && specialPopY === null) {
        specialPopY = cell.y - cell.height / 2 - 10;
      } else if (row.category === "other" && otherY === null) {
        otherY = cell.y - cell.height / 2 - 10;
      }
    }
    // Leftmost X-position for header background
    const xPositions = cells.map((c) => c.x - c.width / 2);
    const leftMostX = Math.min(...xPositions) - 50;

    ctx.font = "bold 14px sans-serif";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    if (demographicY !== null) {
      ctx.fillStyle = "#1E40AF"; // blue-800
      ctx.fillRect(leftMostX, demographicY - 14, 300, 28);
      ctx.fillStyle = "#FFFFFF";
      ctx.fillText("DEMOGRAPHIC GROUPS", leftMostX + 10, demographicY);
    }
    if (specialPopY !== null) {
      ctx.fillStyle = "#9F1239"; // rose-800
      ctx.fillRect(leftMostX, specialPopY - 14, 300, 28);
      ctx.fillStyle = "#FFFFFF";
      ctx.fillText("SPECIAL POPULATIONS", leftMostX + 10, specialPopY);
    }
    if (otherY !== null) {
      ctx.fillStyle = "#374151"; // gray-700
      ctx.fillRect(leftMostX, otherY - 14, 300, 28);
      ctx.fillStyle = "#FFFFFF";
      ctx.fillText("OTHER GROUPS", leftMostX + 10, otherY);
    }
    ctx.restore();
  };

  // 3) Cell Highlighting Layer: highlight cells with largest gaps
  const CellHighlightLayer = (ctx, { cells, margin }) => {
    if (!showHighlighting) return;
    ctx.save();
    if (margin) {
      ctx.translate(margin.left, margin.top);
    }
    cells.forEach((cell) => {
      const data = cell.data;
      if (data?.hasLargestGap) {
        const x = cell.x - cell.width / 2;
        const y = cell.y - cell.height / 2;
        ctx.strokeStyle = "#DC2626"; // red-600
        ctx.lineWidth = 3;
        ctx.strokeRect(x, y, cell.width, cell.height);
        // Add a corner marker
        ctx.fillStyle = "#DC2626";
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + 15, y);
        ctx.lineTo(x, y + 15);
        ctx.closePath();
        ctx.fill();
      }
    });
    ctx.restore();
  };

  // 4) Custom Labels Layer: draws values centered in each cell
  const CustomLabelsLayer = (ctx, { cells, margin }) => {
    const m = margin || { left: 0, top: 0 };
    ctx.save();
    ctx.translate(m.left, m.top);
    cells.forEach((cell) => {
      if (!isNaN(cell.value) && cell.value !== null) {
        const { x, y, value } = cell;
        const formattedValue = `${(value * 100).toFixed(0)}%`;
        ctx.font = "600 14px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = "#FFFFFF";
        ctx.fillText(formattedValue, x, y);
      }
    });
    ctx.restore();
  };

  // 5) Masked Cells Layer: draws dashed outlines and "MASKED" text for cells with insufficient data
  const MaskedCellsLayer = (ctx, { cells, margin }) => {
    const m = margin || { left: 0, top: 0 };
    ctx.save();
    ctx.translate(m.left, m.top);
    cells.forEach((cell) => {
      const { denominator } = cell.data || {};
      if (denominator !== null && denominator < 10) {
        const x = cell.x - cell.width / 2;
        const y = cell.y - cell.height / 2;
        ctx.strokeStyle = "#000000";
        ctx.lineWidth = 1.5;
        ctx.setLineDash([5, 3]);
        ctx.strokeRect(x, y, cell.width, cell.height);
        ctx.setLineDash([]);
        ctx.font = "bold 11px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = "#666666";
        ctx.fillText("MASKED", cell.x, cell.y);
      }
    });
    ctx.restore();
  };

  // 6) Hitbox Layer: store coordinates for hit-testing
  const HitboxLayer = (ctx, { cells }) => {
    const pxRatio = 2;
    const offsetX = heatmapProps.margin.left * pxRatio;
    const offsetY = heatmapProps.margin.top * pxRatio;
    ctx.save();
    cellsRef.current = cells.map((cell) => {
      const dx = (cell.x - cell.width / 2) * pxRatio + offsetX;
      const dy = (cell.y - cell.height / 2) * pxRatio + offsetY;
      const dw = cell.width * pxRatio;
      const dh = cell.height * pxRatio;
      return { ...cell, deviceX: dx, deviceY: dy, deviceW: dw, deviceH: dh };
    });
    ctx.restore();
  };

  // 7) Spacer Cells Layer: draw white boxes for spacer rows
  const SpacerCellsLayer = (ctx, { cells, margin, pixelRatio }) => {
    ctx.save();
    if (pixelRatio) {
      ctx.scale(pixelRatio, pixelRatio);
    }
    if (margin) {
      ctx.translate(margin.left, margin.top);
    }
    cells.forEach((cell) => {
      if (cell.serieId?.includes("spacer")) {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(
          cell.x - cell.width / 2 - 200, // Extended to cover category headers
          cell.y - cell.height / 2,
          cell.width + 200,
          cell.height
        );
      }
    });
    ctx.restore();
  };

  // -----------------------------------------------------------
  // Heatmap configuration
  // -----------------------------------------------------------
  const heatmapProps = {
    margin: { top: topMargin, left: leftMargin, bottom: 50, right: 20 },
    pixelRatio: 2,
    colors: {
      type: "diverging",
      scheme: "red_blue",
      minValue: -0.2,
      maxValue: 0.2,
      divergeAt: 0.5,
    },
    emptyColor: "#F3F4F6", // gray-100
    borderRadius: 0,
    borderWidth: 1,
    borderColor: "#FFFFFF",
    axisTop: {
      tickSize: 5,
      tickPadding: 5,
      tickRotation: -45,
      legend: `Perkins V Indicators (${currentYear})`,
      legendPosition: "middle",
      legendOffset: -35,
    },
    axisLeft: {
      tickSize: 5,
      tickPadding: 5,
      tickRotation: 0,
      legend: "Student Groups",
      legendPosition: "middle",
      legendOffset: -160,
    },
    enableLabels: false,
    isInteractive: false,
    animate: false,
    hoverTarget: "cell",
    cellHoverOpacity: 1,
    cellHoverOthersOpacity: 0.25,
    // Order of layers is important!
    layers: [
      PatternBackgroundLayer,
      "grid",
      "axes",
      "cells",
      SpacerCellsLayer,
      CategoryHeadersLayer,
      CellHighlightLayer,
      MaskedCellsLayer,
      CustomLabelsLayer,
      HitboxLayer,
    ],
  };

  heatmapProps.theme = {
    grid: {
      line: {
        stroke: "#E5E7EB", // gray-200
        strokeWidth: 1,
      },
    },
    tooltip: {
      container: {
        background: "#FFFFFF",
        color: "#1F2937",
        fontSize: 14,
      },
    },
  };

  // -----------------------------------------------------------
  // Manage window mouse events (to hide tooltips on exit)
  // -----------------------------------------------------------
  useEffect(() => {
    const handleWindowMouseMove = (e) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        if (
          e.clientX < rect.left ||
          e.clientX > rect.right ||
          e.clientY < rect.top ||
          e.clientY > rect.bottom
        ) {
          setHoveredContent(null);
        }
      }
    };
    window.addEventListener("mousemove", handleWindowMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleWindowMouseMove);
    };
  }, []);

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
      {/* Header Section */}
      <div className="border-b border-gray-200 p-4">
        <h2 className="text-xl font-bold text-gray-800">{title}</h2>
        <p className="text-gray-600 mt-1">
          Analysis of {currentYear} performance gaps by student demographic subgroups and special populations
        </p>
        {summaryData && (
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top 5 underperforming groups */}
            <div className="border rounded-lg p-3 bg-red-50 border-red-200">
              <h3 className="font-medium text-gray-800 mb-2">Groups with Most Gaps</h3>
              <ul className="text-sm">
                {summaryData.topGroupsWithGaps.map(([group, count]) => (
                  <li key={group} className="flex items-baseline justify-between mb-1">
                    <span>{group}</span>
                    <span className="font-medium text-red-600 ml-2">
                      {count} indicators below target
                    </span>
                  </li>
                ))}
                {summaryData.topGroupsWithGaps.length === 0 && (
                  <li className="text-gray-500">No significant gaps found</li>
                )}
              </ul>
            </div>
            {/* Top 5 indicators with most disparities */}
            <div className="border rounded-lg p-3 bg-blue-50 border-blue-200">
              <h3 className="font-medium text-gray-800 mb-2">Indicators with Most Group Disparities</h3>
              <ul className="text-sm">
                {summaryData.topIndicatorsWithGaps.map(([indicator, count]) => (
                  <li key={indicator} className="flex items-baseline justify-between mb-1">
                    <span>
                      {indicator} - {indicatorDescriptions[indicator] || ""}
                    </span>
                    <span className="font-medium text-blue-600 ml-2">
                      {count} groups below target
                    </span>
                  </li>
                ))}
                {summaryData.topIndicatorsWithGaps.length === 0 && (
                  <li className="text-gray-500">No significant disparities found</li>
                )}
              </ul>
            </div>
          </div>
        )}
      </div>
      {/* Heatmap Container */}
      <Tippy
        content={hoveredContent}
        visible={!!hoveredContent}
        followCursor={true}
        theme="clusterChartBlue"
        plugins={[followCursor]}
        arrow={false}
        offset={[5, 15]}
        appendTo={document.body}
        placement="bottom-start"
        popperOptions={{
          modifiers: [
            { name: "flip", enabled: false },
            { name: "preventOverflow", options: { padding: 20 } },
          ],
        }}
      >
        <div
          ref={containerRef}
          onMouseMove={handleMouseMoveThrottled}
          onMouseLeave={handleMouseLeave}
          style={{ width: containerWidth, height: containerHeight }}
        >
          <ResponsiveHeatMapCanvas
            data={heatmapData}
            {...heatmapProps}
            opacity={1}
            activeOpacity={1}
            inactiveOpacity={1}
          />
        </div>
      </Tippy>
    </div>
  );
};

export default EnhancedPerkinsHeatmap;